/**
 * Permission keys are code-defined and stable. Roles are data (owner-editable) and map to these
 * keys. The SQL seed in `supabase/migrations` must contain exactly this list; a unit test checks it.
 *
 * Naming: `<area>.<action>`. Keys ending in `.approve` are the approver-side half of an approval
 * pair: an employee without `x.perform` may still do `x` if an approver holding `x.approve`
 * enters their PIN on the same terminal.
 */
export type PermissionCategory =
  | 'settings'
  | 'employees'
  | 'roles'
  | 'devices'
  | 'floor'
  | 'production'
  | 'catalog'
  | 'audit'
  | 'reports'
  | 'surfaces'
  | 'orders'
  | 'money'
  | 'shifts';

export interface PermissionDefinition {
  key: PermissionKey;
  category: PermissionCategory;
  description: string;
  /** ADR-028 (D5): held by the OWNER role only; the server refuses it on any other role, seeded or custom. */
  ownerOnly?: true;
}

export const PERMISSION_KEYS = [
  'settings.view',
  'settings.manage',
  'employee.view',
  'employee.manage',
  'role.manage',
  'device.view',
  'device.manage',
  'table.view',
  'table.manage',
  'station.view',
  'station.manage',
  'catalog.view',
  'catalog.manage',
  'audit.view',
  'report.view',
  'report.money',
  'pos.access',
  'kds.access',
  'expo.access',
  'orders.view',
  'order.create',
  'order.send_unpaid',
  'order.void',
  'order.void.approve',
  'refund.perform',
  'refund.allowance',
  'refund.approve',
  'refund.outside_shift',
  'approval.remote',
  'discount.approve',
  'payment.capture',
  'payment.void',
  'order.write_off',
  'writeoff.approve',
  'order.return',
  'kds.operate',
  'serve.operate',
  'serve.correct',
  'table.clear',
  'table.force_clear',
  'shift.operate',
  'cash.move',
  'shift.cover',
  'shift.takeover.approve',
  'cash.receive',
  'shift.review',
  'shift.adjust',
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

export const PERMISSIONS: readonly PermissionDefinition[] = [
  {
    key: 'settings.view',
    category: 'settings',
    description: 'View business, location and system settings',
  },
  {
    key: 'settings.manage',
    category: 'settings',
    description: 'Change business, location and system settings',
  },
  { key: 'employee.view', category: 'employees', description: 'View employees and their roles' },
  {
    key: 'employee.manage',
    category: 'employees',
    description: 'Create, edit, suspend employees; assign roles; set PINs',
  },
  {
    key: 'role.manage',
    category: 'roles',
    description: 'Create and edit roles and their permissions',
  },
  { key: 'device.view', category: 'devices', description: 'View terminals and device health' },
  {
    key: 'device.manage',
    category: 'devices',
    description: 'Enrol, rename, reassign and revoke terminals',
  },
  { key: 'table.view', category: 'floor', description: 'View areas and dining tables' },
  {
    key: 'table.manage',
    category: 'floor',
    description: 'Create, rename, reorder and archive areas and tables',
  },
  {
    key: 'station.view',
    category: 'production',
    description: 'View production routes and stations',
  },
  {
    key: 'station.manage',
    category: 'production',
    description: 'Configure production routes and stations',
  },
  {
    key: 'catalog.view',
    category: 'catalog',
    description: 'Open the catalog: categories, products, variants, modifiers',
  },
  {
    key: 'catalog.manage',
    category: 'catalog',
    description: 'Create, edit, price, route, archive and reorder catalog items; set availability',
  },
  {
    key: 'audit.view',
    category: 'audit',
    description:
      'Read the audit log and the history of every action (shift and cash figures only for whoever reviews shifts)',
  },
  {
    key: 'report.view',
    category: 'reports',
    description: 'Open the reports without money figures (later phase)',
  },
  {
    key: 'report.money',
    category: 'reports',
    description:
      'Read the money reports: sales, takings, payment methods, refunds, payment voids and write-offs',
    ownerOnly: true,
  },
  { key: 'pos.access', category: 'surfaces', description: 'Open the POS, the cashier’s screen' },
  { key: 'kds.access', category: 'surfaces', description: 'Open the Kitchen and Bar screens' },
  {
    key: 'expo.access',
    category: 'surfaces',
    description: 'Open the Expo screen, where orders are served from the pass',
  },
  { key: 'orders.view', category: 'orders', description: 'View orders and the Orders list' },
  {
    key: 'order.create',
    category: 'orders',
    description: 'Place, edit, send and cancel orders on the POS',
  },
  {
    key: 'order.send_unpaid',
    category: 'orders',
    description:
      'Send an order to the kitchen and bar before it is paid; anyone else needs the approval of someone who may',
  },
  {
    key: 'order.void',
    category: 'money',
    description:
      'Void a sent, unserved line or an unserved order (reason required); giving its money back also needs the right to void payments or to approve refunds',
  },
  {
    key: 'order.void.approve',
    category: 'money',
    description: 'Approve another employee’s void by PIN',
  },
  {
    key: 'refund.perform',
    category: 'money',
    description: 'Refund money on a paid order directly (reason required)',
  },
  {
    key: 'refund.allowance',
    category: 'money',
    description:
      'Give money back (a refund or a payment void) without the owner, within the owner’s allowance when it is on; the owner gives it to a role',
  },
  {
    key: 'refund.approve',
    category: 'money',
    description: 'Approve another employee’s refund or payment reversal by PIN',
  },
  {
    key: 'refund.outside_shift',
    category: 'money',
    description:
      'Give money back by GCash or bank transfer from the signed-in account, outside any shift (owner only)',
    ownerOnly: true,
  },
  {
    key: 'approval.remote',
    category: 'money',
    description:
      'Approve a waiting request from the signed-in phone, with the authenticator (owner only)',
    ownerOnly: true,
  },
  {
    key: 'discount.approve',
    category: 'money',
    description: 'Approve discounts by PIN (not used yet)',
  },
  {
    key: 'payment.capture',
    category: 'money',
    description: 'Take payments on the POS (cash, GCash and other enabled methods)',
  },
  {
    key: 'payment.void',
    category: 'money',
    description:
      'Void a payment directly; anyone else needs the approval of someone who approves refunds',
  },
  {
    key: 'order.write_off',
    category: 'money',
    description: 'Write off a walk-out or a staff meal within the allowance; reinstate a write-off',
  },
  {
    key: 'writeoff.approve',
    category: 'money',
    description:
      'Approve a write-off beyond the allowance and give comps (owner only, 6-digit PIN)',
    ownerOnly: true,
  },
  {
    key: 'order.return',
    category: 'money',
    description:
      'Return a served line within the allowance; the money paid for it goes back with the return',
  },
  {
    key: 'kds.operate',
    category: 'production',
    description: 'Start, bump, undo, recall and dismiss tickets on a Kitchen or Bar display',
  },
  {
    key: 'serve.operate',
    category: 'production',
    description:
      'Mark ready tickets and orders served on the Expo or POS, and undo a serve inside the window',
  },
  {
    key: 'serve.correct',
    category: 'production',
    description:
      'Correct a served ticket or order after the undo window (reason required); approves others by PIN',
  },
  {
    key: 'table.clear',
    category: 'floor',
    description: 'Clear an occupied dining table once the guests have left',
  },
  {
    key: 'table.force_clear',
    category: 'floor',
    description: 'Clear a dining table while its orders are still open (reason required)',
  },
  {
    key: 'shift.operate',
    category: 'shifts',
    description:
      'Open, count and close their own shift; hand it over; take over an idle or past-date shift',
  },
  {
    key: 'cash.move',
    category: 'shifts',
    description: 'Record pay-ins, pay-outs, safe drops and no-sales on their own shift',
  },
  {
    key: 'shift.cover',
    category: 'shifts',
    description:
      'Act on another employee’s open shift: sell and give money back on it, close it with their own count',
  },
  {
    key: 'shift.takeover.approve',
    category: 'shifts',
    description: 'Approve by PIN the takeover of a shift that is not idle (6-digit PIN)',
  },
  {
    key: 'cash.receive',
    category: 'shifts',
    description:
      'Receive cash removed from the drawer at a close, and take an owner draw (6-digit PIN)',
  },
  {
    key: 'shift.review',
    category: 'shifts',
    description:
      'See every shift with its figures and summary, also in the audit log (signed-in account only)',
    ownerOnly: true,
  },
  {
    key: 'shift.adjust',
    category: 'shifts',
    description:
      'Manage cash drawers; correct closed shifts and record money missed on them (signed-in account only)',
    ownerOnly: true,
  },
];

/** The permissions no role but OWNER may hold (ADR-028, D5). Mirrors `public.permission.owner_only`. */
export const OWNER_ONLY_PERMISSION_KEYS: readonly PermissionKey[] = PERMISSIONS.filter(
  (p) => p.ownerOnly,
).map((p) => p.key);
export function isOwnerOnlyPermission(key: string): boolean {
  return (OWNER_ONLY_PERMISSION_KEYS as readonly string[]).includes(key);
}

/**
 * System role keys seeded by migration. OWNER is protected; the rest are editable.
 * ADR-022: at didto. Bistro the CASHIER is the trusted shift operator: sends before payment, voids
 * of unserved lines and orders (with a reason), serve corrections and table clears, all without a
 * PIN handoff. Reversing money (payment voids, refunds, voiding a paid order) stays with the OWNER.
 * ADR-024 (Phase 7A): the CASHIER runs their shift and its cash movements; MANAGER can run a shift
 * like a cashier but keeps no money authority (owner decision D7: no payment voids, refunds or
 * refund approvals, never shift.cover), and no daily workflow depends on it. SERVER never touches
 * money. ADR-031 (Phase 7B-1): CASHIER and MANAGER write off walk-outs and staff meals within the
 * location's allowance; beyond it, and every comp, is the owner's (writeoff.approve, owner-only).
 * ADR-035 (Phase 7B-5): no role but OWNER is seeded refund.allowance. The owner gives it (to the Cashier
 * role) and turns the allowance on; then a refund or payment void within it needs no approval. Seeding it
 * would stop MANAGER assigning the Cashier role (no one assigns a role holding a permission they do not
 * hold) while the allowance is still off. CASHIER still never holds payment.void or refund.perform.
 */
export const SEED_ROLE_KEYS = ['OWNER', 'MANAGER', 'CASHIER', 'KITCHEN', 'BAR', 'SERVER'] as const;
export type SeedRoleKey = (typeof SEED_ROLE_KEYS)[number];

export const SEED_ROLE_PERMISSIONS: Record<SeedRoleKey, readonly PermissionKey[]> = {
  OWNER: PERMISSION_KEYS,
  MANAGER: [
    'settings.view',
    'employee.view',
    'employee.manage',
    'device.view',
    'device.manage',
    'table.view',
    'table.manage',
    'station.view',
    'station.manage',
    'catalog.view',
    'catalog.manage',
    'audit.view',
    'report.view',
    'pos.access',
    'kds.access',
    'expo.access',
    'orders.view',
    'order.create',
    'order.send_unpaid',
    'order.void',
    'order.void.approve',
    'discount.approve',
    'payment.capture',
    'order.write_off',
    'order.return',
    'kds.operate',
    'serve.operate',
    'serve.correct',
    'table.clear',
    'table.force_clear',
    'shift.operate',
    'cash.move',
  ],
  CASHIER: [
    'pos.access',
    'orders.view',
    'order.create',
    'order.send_unpaid',
    'order.void',
    'table.view',
    'payment.capture',
    'order.write_off',
    'order.return',
    'serve.operate',
    'serve.correct',
    'table.clear',
    'table.force_clear',
    'shift.operate',
    'cash.move',
  ],
  KITCHEN: ['kds.access', 'kds.operate', 'orders.view'],
  BAR: ['kds.access', 'kds.operate', 'orders.view'],
  SERVER: ['expo.access', 'orders.view', 'table.view', 'serve.operate', 'table.clear'],
};

export type PermissionSet = ReadonlySet<PermissionKey>;

export function toPermissionSet(keys: Iterable<string>): PermissionSet {
  const set = new Set<PermissionKey>();
  for (const k of keys)
    if ((PERMISSION_KEYS as readonly string[]).includes(k)) set.add(k as PermissionKey);
  return set;
}

export function hasPermission(perms: PermissionSet, key: PermissionKey): boolean {
  return perms.has(key);
}

/**
 * Resolve the effective permission set of an employee at one location from role grants.
 * Grants are the (role -> permission keys) map; the employee has exactly one role per location.
 */
export function resolvePermissions(
  roleGrants: ReadonlyMap<string, readonly string[]>,
  roleId: string | null | undefined,
): PermissionSet {
  if (!roleId) return new Set();
  return toPermissionSet(roleGrants.get(roleId) ?? []);
}

export type AuthorizationMode = 'direct' | 'requires_approval' | 'denied';

/**
 * Decide how a sensitive action may proceed for the acting employee.
 * - `direct`: the actor holds the perform permission.
 * - `requires_approval`: the actor lacks it, but an approver with `approveKey` may authorise on the same terminal.
 * - `denied`: no approval path exists for this action.
 */
export function authorizationMode(
  actor: PermissionSet,
  performKey: PermissionKey,
  approveKey: PermissionKey | null,
): AuthorizationMode {
  if (actor.has(performKey)) return 'direct';
  return approveKey ? 'requires_approval' : 'denied';
}
